import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { NormalizedTextBox } from './text-focus.service';
import { CameraTransitionPolicy } from './camera-transition-planner';
import { MAX_HIGHLIGHT_ZOOM } from './highlight-regions';

export type HighlightKind = 'object' | 'text' | 'region';
export type FramingMode = 'fitWithContext' | 'centerAtCurrentScale' | 'none';
export type FocusSource = 'assistant' | 'deepLink' | 'search' | 'internalUi';

export interface PageIdentity {
  planId?: string;
  fileId?: string;
  pageNumber: number;
}

export type FocusGeometry =
  | { kind: 'box'; box: NormalizedTextBox }
  | { kind: 'boxes'; boxes: NormalizedTextBox[] }
  | { kind: 'polygon'; points: Array<{ x: number; y: number }> };

export interface FocusTargetRequest {
  pageIdentity: PageIdentity;
  geometry: FocusGeometry;
  highlightKind: HighlightKind;
  framing?: FramingMode;
  source: FocusSource;
  requestId?: string;
  paddingFraction?: number;
  /**
   * How much closer than the `fitWithContext` framing to show the target (Issue #3256): 2 shows it
   * twice as large, centred on it. Unset or 1 is the standard framing. The Preview's maximum zoom
   * still applies.
   */
  framingZoom?: number;
  label?: string;
  /** Previous same-page geometry, when known, so AUTO can preserve spatial context. */
  sourceGeometry?: FocusGeometry;
  /** Defaults to AUTO; authored playback may force a narrative camera transition. */
  transitionPolicy?: CameraTransitionPolicy;
  /** Optional total camera duration for a deliberately paced user journey. */
  transitionDurationMs?: number;
}

/**
 * The closer-framing factor to apply for a request (Issue #3256): 1 for the standard framing, and
 * at most `MAX_HIGHLIGHT_ZOOM`. A missing, non-finite or not-closer value is the standard framing.
 */
export function focusFramingZoom(request: Pick<FocusTargetRequest, 'framingZoom'>): number {
  const zoom = request.framingZoom;
  return typeof zoom === 'number' && Number.isFinite(zoom) && zoom > 1 ? Math.min(zoom, MAX_HIGHLIGHT_ZOOM) : 1;
}

export type FocusCompletionStatus = 'completed' | 'cancelled' | 'stale' | 'skipped';

export interface FocusResult {
  requestId: string;
  status: FocusCompletionStatus;
  targetScale?: number;
  scrollLeft?: number;
  scrollTop?: number;
  reason?: string;
}

export interface FocusCancellationEvent {
  requestId: string;
  reason: string;
}

/**
 * Shared camera and focus controller service for the Preview workspace.
 * Coordinates smooth zoom, context framing, overlay tracking, and request lifecycle.
 *
 * Related: Issue #1748 - smoothly zoom and frame chat-triggered object and OCR highlights
 */
@Injectable({
  providedIn: 'root',
})
export class PreviewFocusService {
  private requestIdCounter = 0;
  private activeRequest: FocusTargetRequest | null = null;
  private activePromiseResolve: ((result: FocusResult) => void) | null = null;

  private readonly focusRequestSubject = new Subject<FocusTargetRequest>();
  readonly focusRequest$: Observable<FocusTargetRequest> = this.focusRequestSubject.asObservable();

  private readonly focusCancelledSubject = new Subject<FocusCancellationEvent>();
  readonly focusCancelled$: Observable<FocusCancellationEvent> = this.focusCancelledSubject.asObservable();

  /**
   * Dispatches a focus request to the active Preview component and returns a promise
   * that resolves deterministically when the animation settles or is cancelled.
   */
  focusTarget(request: FocusTargetRequest): Promise<FocusResult> {
    const requestId = request.requestId || `focus_${++this.requestIdCounter}_${Date.now()}`;
    const finalizedRequest: FocusTargetRequest = {
      ...request,
      requestId,
      framing: request.framing ?? 'fitWithContext',
      paddingFraction: request.paddingFraction ?? 0.25,
    };

    // Stale previous in-flight request if present
    if (this.activePromiseResolve && this.activeRequest) {
      const prevResolve = this.activePromiseResolve;
      const prevId = this.activeRequest.requestId || '';
      this.activePromiseResolve = null;
      this.activeRequest = null;
      prevResolve({
        requestId: prevId,
        status: 'stale',
        reason: `Superseded by new request ${requestId}`,
      });
    }

    this.activeRequest = finalizedRequest;

    const promise = new Promise<FocusResult>(resolve => {
      this.activePromiseResolve = resolve;
    });

    this.focusRequestSubject.next(finalizedRequest);
    return promise;
  }

  /**
   * Cancels any active or pending focus animation (e.g. on user drag, wheel, or zoom).
   */
  cancelCurrentFocus(reason = 'User interaction'): void {
    if (this.activeRequest && this.activePromiseResolve) {
      const resolve = this.activePromiseResolve;
      const reqId = this.activeRequest.requestId || '';
      this.activePromiseResolve = null;
      this.activeRequest = null;
      this.focusCancelledSubject.next({ requestId: reqId, reason });
      resolve({
        requestId: reqId,
        status: 'cancelled',
        reason,
      });
    }
  }

  /**
   * Internal completion notification invoked by PreviewComponent once the transition
   * and canvas commit are complete.
   */
  notifyFocusCompleted(result: FocusResult): void {
    if (this.activeRequest && this.activeRequest.requestId === result.requestId) {
      if (this.activePromiseResolve) {
        this.activePromiseResolve(result);
        this.activePromiseResolve = null;
      }
      this.activeRequest = null;
    }
  }

  /** Returns the active focus request, if any is pending or animating. */
  getActiveRequest(): FocusTargetRequest | null {
    return this.activeRequest;
  }

  /**
   * Computes a unified, normalized bounding box [0, 1] from any supported FocusGeometry.
   * Handles out-of-bounds, unscaled PDF point coordinates, zero-size, and malformed values.
   */
  static computeUnionBox(
    geometry: FocusGeometry,
    pageDimensions?: { width: number; height: number } | null
  ): NormalizedTextBox {
    if (!geometry) {
      return { x: 0.25, y: 0.25, width: 0.5, height: 0.5 };
    }

    let boxes: NormalizedTextBox[] = [];

    if (geometry.kind === 'box') {
      boxes = [geometry.box];
    } else if (geometry.kind === 'boxes') {
      boxes = geometry.boxes || [];
    } else if (geometry.kind === 'polygon') {
      const pts = geometry.points || [];
      if (pts.length > 0) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const p of pts) {
          if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x);
            maxY = Math.max(maxY, p.y);
          }
        }
        if (Number.isFinite(minX) && Number.isFinite(maxX)) {
          boxes = [{ x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) }];
        }
      }
    }

    if (boxes.length === 0) {
      return { x: 0.25, y: 0.25, width: 0.5, height: 0.5 };
    }

    // Normalize each box to [0, 1]
    const normalizedBoxes = boxes.map(b => PreviewFocusService.normalizeSingleBox(b, pageDimensions));

    let uMinX = Infinity;
    let uMinY = Infinity;
    let uMaxX = -Infinity;
    let uMaxY = -Infinity;

    for (const b of normalizedBoxes) {
      uMinX = Math.min(uMinX, b.x);
      uMinY = Math.min(uMinY, b.y);
      uMaxX = Math.max(uMaxX, b.x + b.width);
      uMaxY = Math.max(uMaxY, b.y + b.height);
    }

    if (!Number.isFinite(uMinX) || !Number.isFinite(uMaxX)) {
      return { x: 0.25, y: 0.25, width: 0.5, height: 0.5 };
    }

    // Clamp union to [0, 1]
    const clampedMinX = Math.max(0, Math.min(1, uMinX));
    const clampedMinY = Math.max(0, Math.min(1, uMinY));
    const clampedMaxX = Math.max(clampedMinX, Math.min(1, uMaxX));
    const clampedMaxY = Math.max(clampedMinY, Math.min(1, uMaxY));

    // Ensure non-zero minimum dimension
    const minDim = 0.005;
    const width = Math.max(minDim, clampedMaxX - clampedMinX);
    const height = Math.max(minDim, clampedMaxY - clampedMinY);

    return {
      x: Math.min(clampedMinX, 1 - width),
      y: Math.min(clampedMinY, 1 - height),
      width,
      height,
    };
  }

  private static normalizeSingleBox(
    box: NormalizedTextBox,
    pageDimensions?: { width: number; height: number } | null
  ): NormalizedTextBox {
    if (!box) {
      return { x: 0.25, y: 0.25, width: 0.5, height: 0.5 };
    }

    let x = Number.isFinite(box.x) ? box.x : 0;
    let y = Number.isFinite(box.y) ? box.y : 0;
    let width = Number.isFinite(box.width) ? Math.max(0, box.width) : 0.1;
    let height = Number.isFinite(box.height) ? Math.max(0, box.height) : 0.1;

    // Check if coordinates are in PDF points (> 1.0) and convert using page dimensions
    const isUnscaledPdfPoints =
      pageDimensions &&
      pageDimensions.width > 0 &&
      pageDimensions.height > 0 &&
      (x > 1.0 || y > 1.0 || width > 1.0 || height > 1.0);

    if (isUnscaledPdfPoints && pageDimensions) {
      x = x / pageDimensions.width;
      y = y / pageDimensions.height;
      width = width / pageDimensions.width;
      height = height / pageDimensions.height;
    } else if (x > 1.0 || y > 1.0 || width > 1.0 || height > 1.0) {
      // Possible 0..1000 scale (common in hierarchy regions)
      if (x <= 1000 && y <= 1000 && width <= 1000 && height <= 1000) {
        x = x / 1000;
        y = y / 1000;
        width = width / 1000;
        height = height / 1000;
      }
    }

    const clampedX = Math.max(0, Math.min(1, x));
    const clampedY = Math.max(0, Math.min(1, y));
    const clampedW = Math.max(0.001, Math.min(1 - clampedX, width));
    const clampedH = Math.max(0.001, Math.min(1 - clampedY, height));

    return {
      x: clampedX,
      y: clampedY,
      width: clampedW,
      height: clampedH,
      rotationDegrees: box.rotationDegrees,
    };
  }
}
